import { useEffect, useState, useCallback } from "react";
import { adminLogin, getToken, clearToken, cfoHoursSheet, cfoSetTimeRate, formatApiError } from "@/lib/api";

const hrs = (v) => (v == null ? "—" : `${Number(v).toFixed(2)}h`);
const money = (v, c = "USD") => (v == null ? "—" : `${c === "USD" ? "$" : ""}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${c !== "USD" ? " " + c : ""}`);
const dateLabel = (iso) => {
  try { return new Date(iso + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" }); }
  catch { return iso; }
};

const Chip = ({ v, testid }) => (
  <span data-testid={testid} className="text-[10px] px-2 py-0.5 rounded-[6px] font-semibold tracking-wide"
    style={{ background: "rgba(180,139,63,0.12)", color: "#c8a24a", border: "1px solid rgba(180,139,63,0.25)" }}>
    {v ? String(v).replace(/_/g, " ") : v}
  </span>
);

const WeekCard = ({ w, idx, currency }) => {
  const maxDay = Math.max(1, ...w.days.map((d) => d.minutes));
  return (
    <div data-testid={`hours-week-${idx}`}
      className="rounded-[10px] border border-[rgba(180,139,63,0.18)] bg-[rgba(20,18,14,0.55)] p-5"
      style={w.is_current_week ? { boxShadow: "0 0 0 1px rgba(180,139,63,0.4)" } : {}}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-[13px] uppercase tracking-[0.14em] text-[#c8a24a]">
            {w.iso_week} {w.is_current_week && <span className="text-[10px] text-[#8fd19e]">· current</span>}
          </h3>
          <p className="text-[11px] text-[var(--muted)] mt-0.5">{dateLabel(w.week_start)} – {dateLabel(w.week_end)}</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-semibold" data-testid={`hours-week-total-${idx}`}>{hrs(w.total_hours)}</div>
          {w.estimated_value != null && <div className="text-[12px] text-[#c8a24a]" data-testid={`hours-week-value-${idx}`}>≈ {money(w.estimated_value, currency)}</div>}
          <div className="text-[11px] text-[var(--muted)]">{w.session_count} session{w.session_count === 1 ? "" : "s"}</div>
        </div>
      </div>

      {/* Day order follows the saved Founder profile. */}
      <div className="grid grid-cols-7 gap-1.5 mb-4" data-testid={`hours-grid-${idx}`}>
        {w.days.map((d, i) => (
          <div key={i} className="text-center" title={`${d.day} ${dateLabel(d.date)}: ${hrs(d.hours)}`}>
            <div className="text-[10px] text-[var(--muted)] mb-1">{d.day}</div>
            <div className="h-16 rounded-[6px] border border-[rgba(180,139,63,0.15)] flex items-end overflow-hidden bg-[rgba(0,0,0,0.2)]">
              <div className="w-full rounded-[4px]"
                style={{ height: `${Math.round((d.minutes / maxDay) * 100)}%`, minHeight: d.minutes > 0 ? "6px" : "0",
                  background: "linear-gradient(180deg, rgba(200,162,74,0.85), rgba(180,139,63,0.5))" }} />
            </div>
            <div className="text-[10px] mt-1 font-medium" data-testid={`hours-day-${idx}-${i}`}>{d.hours > 0 ? d.hours.toFixed(1) : "·"}</div>
          </div>
        ))}
      </div>

      {/* Per-business (entity) totals */}
      <div className="border-t border-[rgba(180,139,63,0.1)] pt-2">
        <p className="text-[10px] uppercase tracking-[0.14em] text-[var(--muted)] mb-1.5">By business</p>
        {Object.keys(w.by_entity || {}).length === 0 && w.unattributed_hours === 0 && (
          <p className="text-[12px] text-[var(--muted)]">No attributed hours this week.</p>
        )}
        {Object.entries(w.by_entity || {}).sort((a, b) => b[1] - a[1]).map(([ent, v]) => (
          <div key={ent} className="flex items-center justify-between py-0.5" data-testid={`hours-entity-${idx}-${ent}`}>
            <span className="text-[12px] text-[var(--fg)]">{ent}</span>
            <span className="text-[12px] font-medium">{hrs(v)}{w.by_entity_value && w.by_entity_value[ent] != null && <span className="text-[#c8a24a]"> · {money(w.by_entity_value[ent], currency)}</span>}</span>
          </div>
        ))}
        {w.unattributed_hours > 0 && (
          <div className="flex items-center justify-between py-0.5">
            <span className="text-[12px] text-[var(--muted)]">Unattributed</span>
            <span className="text-[12px] font-medium text-[#e7a877]">{hrs(w.unattributed_hours)}</span>
          </div>
        )}
      </div>
    </div>
  );
};

function Console({ onSignOut }) {
  const [data, setData] = useState(null);
  const [weeks, setWeeks] = useState(8);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [rateInput, setRateInput] = useState("");
  const [savingRate, setSavingRate] = useState(false);

  const load = useCallback(async (n) => {
    setLoading(true); setErr("");
    try { const d = await cfoHoursSheet(n); setData(d); if (d.rate_per_hour != null) setRateInput(String(d.rate_per_hour)); }
    catch (e) { setErr(formatApiError(e?.response?.data?.detail, "Failed to load the hours sheet.")); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(weeks); }, [load, weeks]);

  const saveRate = async (val) => {
    if (savingRate) return;
    const raw = (typeof val === "string" ? val : (rateInput || "0")).trim();
    setSavingRate(true); setErr("");
    try { await cfoSetTimeRate(raw || "0", "USD"); if (raw === "0" || raw === "") setRateInput(""); await load(weeks); }
    catch (e) { setErr(formatApiError(e?.response?.data?.detail, "Failed to save the rate.")); }
    finally { setSavingRate(false); }
  };

  const empty = data && data.state === "DATA_NOT_AVAILABLE";

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--fg)] px-6 py-8" data-testid="hours-console">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold" style={{ fontFamily: "Cinzel, serif" }}>JMH — Founder Hours Sheet</h1>
            <p className="text-[13px] text-[var(--muted)] mt-1">
              Auto-derived weekly hours · {data?.week_start_day === "SUNDAY" ? "Sunday–Saturday" : "Monday–Sunday"} · {data?.timezone || "America/Anchorage"} · system/agent time excluded
              {data && <> · <Chip v={data.capture_source} testid="hours-capture-source" /></>}
            </p>
          </div>
          <button onClick={onSignOut} data-testid="hours-signout" className="text-[12px] text-[var(--muted)] hover:text-[#c8a24a]">Sign out</button>
        </div>

        <div className="flex items-center gap-3 mb-6 flex-wrap" data-testid="hours-controls">
          <span className="text-[12px] text-[var(--muted)]">History:</span>
          {[4, 8, 12, 26].map((n) => (
            <button key={n} data-testid={`hours-weeks-${n}`} onClick={() => setWeeks(n)}
              className="text-[12px] px-3 py-1.5 rounded-[8px] border"
              style={{ borderColor: "rgba(180,139,63,0.3)", background: weeks === n ? "rgba(180,139,63,0.18)" : "transparent", color: weeks === n ? "#e6d8b0" : "var(--muted)" }}>
              {n} weeks
            </button>
          ))}
          {data && (
            <span className="ml-auto text-[13px]" data-testid="hours-grand-total">
              <span className="text-[var(--muted)]">Total ({data.weeks_returned}w): </span>
              <b>{hrs(data.historical_total_hours)}</b>
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 mb-6 flex-wrap" data-testid="hours-rate-bar">
          <span className="text-[12px] text-[var(--muted)]">Hourly rate:</span>
          <div className="flex items-center gap-1">
            <span className="text-[13px] text-[var(--muted)]">$</span>
            <input data-testid="hours-rate-input" value={rateInput} onChange={(e) => setRateInput(e.target.value)} placeholder="0.00" inputMode="decimal"
              className="w-24 px-2 py-1 text-[13px] rounded-[8px] bg-transparent border border-[rgba(180,139,63,0.3)]" />
          </div>
          <button data-testid="hours-rate-save" onClick={() => saveRate()} disabled={savingRate}
            className="text-[12px] px-3 py-1.5 rounded-[8px] border border-[rgba(180,139,63,0.4)] bg-[rgba(180,139,63,0.15)] disabled:opacity-50">
            {savingRate ? "Saving…" : "Set rate"}
          </button>
          {data && data.economic_value === "ESTIMATED_FROM_FOUNDER_RATE" && (
            <button data-testid="hours-rate-clear" onClick={() => saveRate("0")} disabled={savingRate}
              className="text-[12px] px-3 py-1.5 rounded-[8px] border border-[rgba(180,139,63,0.25)] text-[var(--muted)] disabled:opacity-50">
              Clear
            </button>
          )}
          {data && data.economic_value === "ESTIMATED_FROM_FOUNDER_RATE" && data.estimated_value_total != null ? (
            <span className="ml-auto text-[13px]" data-testid="hours-value-total">
              <span className="text-[var(--muted)]">Est. value ({data.weeks_returned}w): </span>
              <b className="text-[#c8a24a]">≈ {money(data.estimated_value_total, data.currency)}</b>
            </span>
          ) : (
            <span className="ml-auto text-[11px] text-[var(--muted)]" data-testid="hours-rate-hint">Set an hourly rate to estimate the value of recorded time. Hours remain visible without a rate; calculated value is an estimate and does not represent wages paid.</span>
          )}
        </div>

        {err && <p className="text-[#e88] text-sm mb-4" data-testid="hours-error">{err}</p>}
        {loading && !data && <p className="text-[var(--muted)] text-sm" data-testid="hours-loading">Loading…</p>}
        {empty && !err && (
          <div className="rounded-[10px] border border-[rgba(180,139,63,0.18)] bg-[rgba(20,18,14,0.55)] p-8 text-center" data-testid="hours-empty">
            <p className="text-[15px] mb-1">No Founder-active hours captured yet.</p>
            <p className="text-[13px] text-[var(--muted)]">Hours are derived from recorded Founder activity as supporting evidence arrives. This view summarizes that evidence rather than functioning as a manually completed timesheet.</p>
          </div>
        )}

        {data && !empty && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="hours-weeks">
            {data.weeks.map((w, i) => <WeekCard key={w.week_start} w={w} idx={i} currency={data.currency} />)}
          </div>
        )}

        <p className="text-[11px] text-[var(--muted)] mt-6" data-testid="hours-footnote">
          Auto-captured from Founder-active evidence · multi-business overlap allocated once (never doubled) ·
          {" "}economic value: {data && data.economic_value === "ESTIMATED_FROM_FOUNDER_RATE"
            ? "ESTIMATED (Founder rate × auto-captured hours) — not actual paid/accrued compensation"
            : "rate not established"}.
        </p>
      </div>
    </div>
  );
}

function Login({ onIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setErr("");
    try { await adminLogin(email, password); onIn(); }
    catch { setErr("Login failed."); setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] text-[var(--fg)]">
      <form onSubmit={submit} className="w-full max-w-sm p-6 rounded-[10px] border border-[rgba(180,139,63,0.2)]" data-testid="hours-login-form">
        <h1 className="text-xl mb-4" style={{ fontFamily: "Cinzel, serif" }}>JMH Hours — Sign in</h1>
        <input data-testid="hours-login-email" type="email" autoComplete="username" className="w-full mb-3 px-3 py-2 rounded-[8px] bg-transparent border border-[rgba(180,139,63,0.3)]" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input data-testid="hours-login-password" type="password" autoComplete="current-password" className="w-full mb-3 px-3 py-2 rounded-[8px] bg-transparent border border-[rgba(180,139,63,0.3)]" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
        {err && <p data-testid="hours-login-error" className="text-[#e88] text-sm mb-3">{err}</p>}
        <button data-testid="hours-login-submit" disabled={busy} className="w-full py-2 rounded-[8px] bg-[rgba(180,139,63,0.2)] border border-[rgba(180,139,63,0.4)] disabled:opacity-50">{busy ? "Signing in…" : "Enter"}</button>
      </form>
    </div>
  );
}

export default function FounderHoursSheet() {
  return <Console onSignOut={clearToken} />;
}
